import React from 'react';

interface InfoTooltipProps {
  title: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * InfoTooltip - A hover tooltip component for explaining technical terms
 * Hover over the (i) icon to see the explanation
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({ title, children, position = 'top' }) => {
  return (
    <span className="info-tooltip">
      <span className="info-icon">i</span>
      <span className={`tooltip-popup tooltip-${position}`}>
        <span className="tooltip-title">{title}</span>
        <span className="tooltip-text">{children}</span>
      </span>
    </span>
  );
};

// Predefined tooltips for common mining/finance terms
export const TERM_EXPLANATIONS = {
  EBIT: {
    title: "EBIT - Earnings Before Interest & Taxes",
    text: "This is the company's operating surplus after all costs (mining, processing, labor, equipment) but before paying interest on loans or taxes. It represents the true operational performance of the mine."
  },
  GOLD_GRADE: {
    title: "In-situ Grade (g/m³)",
    text: "The amount of gold naturally present in each cubic meter of ore. A grade of 0.8 g/m³ means for every cubic meter of rock processed, we expect to recover about 0.8 grams of gold."
  },
  RECOVERY_RATE: {
    title: "Recovery Rate",
    text: "The percentage of gold successfully extracted from the ore during processing. A 95% recovery rate means we capture 95% of the gold present in the rock. Industry standard ranges from 85-98%."
  },
  THROUGHPUT: {
    title: "Throughput (t/hr)",
    text: "The amount of ore material a processing plant can handle per hour, measured in tonnes. Higher throughput means more material processed and potentially more gold recovered."
  },
  DIVIDEND: {
    title: "Dividend Per Share",
    text: "The portion of company earnings paid to each shareholder. Calculated by dividing total distributable earnings (EBIT) by the total number of shares outstanding."
  },
  MODULAR_PLANT: {
    title: "Modular Processing Plant",
    text: "A self-contained gold extraction unit that can be easily deployed and scaled. Each plant operates independently, allowing the operation to grow by adding more plants."
  },
  OPEX: {
    title: "Operating Expenses (OPEX)",
    text: "The ongoing costs to run the mining operation including labor, fuel, chemicals, maintenance, and administration. Expressed as a percentage of revenue."
  },
  GOLD_PRICE: {
    title: "Gold Price (USD/kg)",
    text: "The current market price for one kilogram of pure gold. Gold prices fluctuate based on global demand, economic conditions, and currency values. 1 kg = 32.15 troy ounces."
  },
  DIVIDEND_YIELD: {
    title: "Dividend Yield",
    text: "The percentage dividend you can expect on your share purchase over a period. Calculated as (Total Dividends ÷ Initial Purchase) × 100. Higher yield means better dividends."
  },
  SHARES: {
    title: "Total Shares Outstanding",
    text: "The total number of company shares issued and held by all shareholders. Dividends are divided equally among all shares, so owning more shares means receiving more dividends."
  }
};

export default InfoTooltip;

